// src/pages/LeadsOverview.jsx
import React, { useState } from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Button,
  Container,
  createTheme,
  ThemeProvider,
  useMediaQuery,
  Stack,
  Chip,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { Refresh, Today } from '@mui/icons-material';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#F96168' },
    secondary: { main: '#000000' },
    background: { default: '#f8fafc' }
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(',')
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px'
        }
      }
    }
  }
});

export default function LeadsOverview() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tableHeight = isMobile ? '400px' : 'calc(100vh - 300px)';

  const StatusChip = ({ value }) => {
    const map = {
      completed: { color: '#10b981', bg: '#ecfdf5', label: 'COMPLETED' },
      active:    { color: '#F96168', bg: '#FCF3F3', label: 'ACTIVE' },
      abandoned: { color: '#ef4444', bg: '#fef2f2', label: 'ABANDONED' },
    };
    const s = map[value] || { color: '#64748b', bg: '#e2e8f0', label: String(value || '').toUpperCase() };
    return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, px: 1 }} />;
  };

  const ProgressCell = ({ pct }) => (
    <Tooltip title={`${pct}%`}>
      <Box sx={{ width: 88, bgcolor: '#e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: 8, bgcolor: '#F96168' }} />
      </Box>
    </Tooltip>
  );

  const columns = [
    { accessorKey: 'lead_id', header: 'Lead ID', size: 120,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{cell.getValue()}</Typography>
    },
    { accessorKey: 'session_id', header: 'Session', size: 240,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cell.getValue() || '—'}</Typography>
    },
    { accessorKey: 'status', header: 'Status', size: 130,
      Cell: ({ cell }) => <StatusChip value={cell.getValue()} />
    },
    { accessorKey: 'answers_count', header: 'Answers', size: 90 },
    { accessorKey: 'total_questions', header: 'Questions', size: 100 },
    { accessorKey: 'progress_pct', header: 'Progress', size: 120,
      Cell: ({ cell }) => <ProgressCell pct={cell.getValue() || 0} />
    },
    { accessorKey: 'total_score', header: 'Score', size: 90 },
    { accessorKey: 'started_at', header: 'Started', size: 160,
      Cell: ({ cell }) => cell.getValue() ? dayjs(cell.getValue()).format('DD MMM YY · HH:mm') : '—'
    },
    { accessorKey: 'last_seen_at', header: 'Last Seen', size: 160,
      Cell: ({ cell }) => cell.getValue() ? dayjs(cell.getValue()).format('DD MMM YY · HH:mm') : '—'
    },
    { accessorKey: 'ended_at', header: 'Ended', size: 160,
      Cell: ({ cell }) => cell.getValue() ? dayjs(cell.getValue()).format('DD MMM YY · HH:mm') : '—'
    },
  ];

  const handleFetch = async () => {
    setError(null);
    setLoading(true);
    try {
      const s = startDate.format('YYYY-MM-DD');
      const e = endDate.format('YYYY-MM-DD');
      const resp = await fetch(`/leads/api/leads/overview?start=${s}&end=${e}`);
      if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);
      const result = await resp.json();
      setRows(result || []);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSelect = (days) => {
    setStartDate(dayjs().subtract(days, 'day'));
    setEndDate(dayjs());
  };

  const hiddenColumns = isMobile
    ? ['session_id', 'ended_at', 'answers_count', 'total_questions', 'last_seen_at']
    : [];

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: { xs: 'auto', lg: '100vh' } }}>
          <Stack spacing={3} sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" fontWeight={700}>Leads Overview</Typography>
              <Button
                variant="contained"
                onClick={handleFetch}
                disabled={loading}
                startIcon={<Refresh />}
                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                {loading ? 'Refreshing...' : 'Fetch Data'}
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slots={{ openPickerIcon: Today }}
                    slotProps={{ textField: { size: 'small', fullWidth: isMobile } }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slots={{ openPickerIcon: Today }}
                    slotProps={{ textField: { size: 'small', fullWidth: isMobile } }}
                  />
                  <Stack direction="row" spacing={1} sx={{ ml: isMobile ? 0 : 'auto', flexWrap: 'wrap', gap: 1 }}>
                    {[7, 30, 60].map((d) => (
                      <Chip
                        key={d}
                        label={`Last ${d} days`}
                        onClick={() => handleQuickSelect(d)}
                        variant={dayjs().diff(startDate, 'day') === d ? 'filled' : 'outlined'}
                        color="primary"
                        clickable
                      />
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>Error fetching data: {error}</Alert>}

            {/* Table */}
            <Paper sx={{ position: 'relative', overflow: 'hidden' }}>
              {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
              <MaterialReactTable
                columns={columns}
                data={rows}
                enableColumnActions={false}
                enableSorting
                enablePagination
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                initialState={{
                  density: 'compact',
                  hiddenColumns,
                  pagination: { pageSize: isMobile ? 5 : 10 }
                }}
                muiTablePaperProps={{ elevation: 0 }}
                muiTableContainerProps={{ sx: { maxHeight: tableHeight } }}
                muiTableHeadProps={{ sx: { '& th': { fontWeight: 600, bgcolor: '#f8fafc' } } }}
                muiTableBodyProps={{ sx: { '& tr:hover td': { bgcolor: '#f8fafc' } } }}
                renderTopToolbarCustomActions={() => (
                  <Typography variant="body2" color="text.secondary">
                    Showing {rows.length} leads
                  </Typography>
                )}
              />
            </Paper>
          </Stack>
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
